import { z } from 'zod';
import type { StatusToken } from '@sleed/bizcommerce-api';
import { toIsoUtc } from '~/lib/format-date';

/**
 * Product status edit form. Mirrors `StatusToken` and the legacy admin popup
 * (Core/client/admin/src/pages/Catalog/Statuses/index.js): nm required ≤100, cd/eurl/canonical_url
 * optional, dshc/iv/s_as_tag/disable_* booleans, si required, chex/chex_text colors, h1/mt/mk/md SEO,
 * bd HTML body, tooltip (storefront hover text, BW-8300), startDate/endDate scheduling, img1 image. `nm` is multilanguage server-side (writes go
 * to the active Accept-Language, default `el`). Image bytes are uploaded out-of-band (img1File →
 * binaryRepoImagesApi.uploadStatus, needs a saved id). `erpcd` is not surfaced in the UI (parity with
 * the legacy form) but round-trips so edits don't drop the ERP sync code.
 */
const nullableStr = z.string().nullable();

export const statusSchema = z.object({
	id: z.number().optional(),
	nm: z.string().trim().min(1, 'validation.required').max(100, 'validation.tooLong'),
	cd: nullableStr,
	eurl: z.string().trim().max(400, 'validation.tooLong').nullable(),
	dshc: z.boolean(),
	iv: z.boolean(),
	si: z.number({ message: 'validation.required' }).int(),
	s_as_tag: z.boolean(),
	chex: nullableStr,
	chex_text: nullableStr,
	disable_indexation: z.boolean(),
	disable_follow: z.boolean(),
	canonical_url: nullableStr,
	tooltip: nullableStr,
	h1: nullableStr,
	mt: nullableStr,
	mk: nullableStr,
	md: nullableStr,
	bd: nullableStr,
	startDate: nullableStr,
	endDate: nullableStr,
	img1: nullableStr,
	imgdel1: z.boolean(),
	// Staged upload blob (uploaded out-of-band after save), and ERP code carried through unchanged.
	img1File: z.instanceof(File).nullable(),
	erpcd: nullableStr
});

export type StatusFormValues = z.infer<typeof statusSchema>;

export const EMPTY_STATUS: StatusFormValues = {
	nm: '',
	cd: null,
	eurl: null,
	dshc: false,
	iv: true,
	si: 0,
	s_as_tag: false,
	chex: null,
	chex_text: null,
	disable_indexation: false,
	disable_follow: false,
	canonical_url: null,
	tooltip: null,
	h1: null,
	mt: null,
	mk: null,
	md: null,
	bd: null,
	startDate: null,
	endDate: null,
	img1: null,
	imgdel1: false,
	img1File: null,
	erpcd: null
};

export function tokenToForm(token: StatusToken): StatusFormValues {
	return {
		id: token.id,
		nm: token.nm ?? '',
		cd: token.cd ?? null,
		eurl: token.eurl ?? null,
		dshc: token.dshc ?? false,
		iv: token.iv ?? false,
		si: token.si ?? 0,
		s_as_tag: token.s_as_tag ?? false,
		chex: token.chex ?? null,
		chex_text: token.chex_text ?? null,
		disable_indexation: token.disable_indexation ?? false,
		disable_follow: token.disable_follow ?? false,
		canonical_url: token.canonical_url ?? null,
		tooltip: token.tooltip ?? null,
		h1: token.h1 ?? null,
		mt: token.mt ?? null,
		mk: token.mk ?? null,
		md: token.md ?? null,
		bd: token.bd ?? null,
		startDate: toIsoUtc(token.startDate),
		endDate: toIsoUtc(token.endDate),
		img1: token.img1 ?? null,
		imgdel1: false,
		img1File: null,
		erpcd: token.erpcd ?? null
	};
}

export function formToToken(form: StatusFormValues): StatusToken {
	return {
		id: form.id,
		nm: form.nm.trim(),
		cd: form.cd,
		eurl: form.eurl,
		dshc: form.dshc,
		iv: form.iv,
		si: form.si,
		s_as_tag: form.s_as_tag,
		chex: form.chex,
		chex_text: form.chex_text,
		disable_indexation: form.disable_indexation,
		disable_follow: form.disable_follow,
		canonical_url: form.canonical_url,
		tooltip: form.tooltip?.trim() || null,
		h1: form.h1,
		mt: form.mt,
		mk: form.mk,
		md: form.md,
		bd: form.bd,
		startDate: form.startDate,
		endDate: form.endDate,
		img1: form.img1,
		imgdel1: form.imgdel1,
		erpcd: form.erpcd
	};
}
